import { useContext, useEffect, useMemo, useState } from "react";
import { UserContext } from "@/app/_components/context/user-context";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

export enum LeaderboardType {
  SALES = "SALES",
  MANAGER = "MANAGER",
  FINANCE = "FINANCE",
}

type ManagerLeaderboardData = {
  [key: string]: {
    units: number;
    newGross: number;
    usedGross: number;
    totalGross: number;
  };
};

type SalesLeaderboardData = {
  [key: string]: {
    newUnits: number;
    usedUnits: number;
    totalUnits: number;
  };
};

interface LeaderboardCardProps {
  loading: boolean;
  gtgOnly: boolean;
  primaryUserGroup: string;
  overviewRange: "MTD" | "WTD" | "YTD";
}

const LABELS: Record<LeaderboardType, string> = {
  [LeaderboardType.SALES]: "Sales Person",
  [LeaderboardType.MANAGER]: "Sales Manager",
  [LeaderboardType.FINANCE]: "Finance Manager",
};

const kFormatter = (num: number) =>
  Math.abs(num) > 999
    ? Math.sign(num) * Number((Math.abs(num) / 1000).toFixed(1)) + "K"
    : Math.sign(num) * Math.abs(num);

export const MobileLeaderboardCard = ({
  loading,
  gtgOnly,
  primaryUserGroup,
  overviewRange,
}: LeaderboardCardProps) => {
  const [leaderboardData, setLeaderboardData] = useState<
    ManagerLeaderboardData | SalesLeaderboardData
  >();
  const [leaderboardType, setLeaderboardType] = useState<LeaderboardType>(
    LeaderboardType.SALES,
  );

  const isSalesPerson =
    primaryUserGroup === "Sales Person" ||
    primaryUserGroup === "Parts Advisor / Other" ||
    primaryUserGroup === "Service Advisor";

  useEffect(() => {
    const fetchLeaderboardData = async () => {
      if (
        leaderboardType === LeaderboardType.FINANCE ||
        leaderboardType === LeaderboardType.MANAGER
      ) {
        const response = await fetch(
          `/v2/api/reporting/managers-leaderboard?range=${overviewRange}&gtg=${gtgOnly}&type=${leaderboardType}`,
        );
        if (!response.ok) return;
        const data: ManagerLeaderboardData = await response.json();
        const ranked = Object.entries(data)
          .sort((a, b) => b[1].totalGross - a[1].totalGross)
          .reduce(
            (acc, [key, value]) => ({ ...acc, [key]: value }),
            {} as ManagerLeaderboardData,
          );
        setLeaderboardData(ranked);
      } else {
        const response = await fetch(
          `/v2/api/reporting/sales-leaderboard?range=${overviewRange}&gtg=${gtgOnly}`,
        );
        if (!response.ok) return;
        const data: SalesLeaderboardData = await response.json();
        const ranked = Object.entries(data)
          .sort((a, b) => b[1].totalUnits - a[1].totalUnits)
          .reduce(
            (acc, [key, value]) => ({ ...acc, [key]: value }),
            {} as SalesLeaderboardData,
          );
        setLeaderboardData(ranked);
      }
    };

    fetchLeaderboardData();
  }, [leaderboardType, overviewRange, gtgOnly]);

  return (
    <div className="flex flex-col gap-3">
      {!isSalesPerson && (
        <Select
          value={leaderboardType}
          onValueChange={(val) => setLeaderboardType(val as LeaderboardType)}
        >
          <SelectTrigger className="h-8 text-xs w-full">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value={LeaderboardType.SALES} className="text-xs">
              {LABELS[LeaderboardType.SALES]}
            </SelectItem>
            <SelectItem value={LeaderboardType.MANAGER} className="text-xs">
              {LABELS[LeaderboardType.MANAGER]}
            </SelectItem>
            <SelectItem value={LeaderboardType.FINANCE} className="text-xs">
              {LABELS[LeaderboardType.FINANCE]}
            </SelectItem>
          </SelectContent>
        </Select>
      )}

      {loading ? (
        <div className="flex flex-col gap-2">
          {[...Array(5)].map((_, i) => (
            <Skeleton key={i} className="h-6 w-full" />
          ))}
        </div>
      ) : (
        <table className="w-full text-sm">
          <thead>
            <tr className="text-muted-foreground text-xs border-b">
              <th className="text-center pb-1 w-8">#</th>
              <th className="text-left pb-1">Name</th>
              {leaderboardType !== LeaderboardType.SALES && (
                <th className="text-center pb-1">Units</th>
              )}
              <th className="text-center pb-1">
                {leaderboardType === LeaderboardType.SALES ? "Total" : "Gross"}
              </th>
            </tr>
          </thead>
          <tbody>
            {leaderboardData &&
              Object.entries(leaderboardData).map(([key, value], index) => (
                <tr
                  key={key}
                  className={`text-xs py-1 ${
                    index === 0
                      ? "text-green-500 font-semibold"
                      : "text-foreground"
                  }`}
                >
                  <td className="text-center py-1">{index + 1}</td>
                  <td className="text-left py-1">{key}</td>
                  {leaderboardType !== LeaderboardType.SALES && (
                    <td className="text-center py-1">
                      {(value as ManagerLeaderboardData[string]).units}
                    </td>
                  )}
                  <td className="text-center py-1">
                    {leaderboardType === LeaderboardType.SALES
                      ? (value as SalesLeaderboardData[string]).totalUnits
                      : `$${kFormatter((value as ManagerLeaderboardData[string]).totalGross)}`}
                  </td>
                </tr>
              ))}
          </tbody>
        </table>
      )}
    </div>
  );
};

export default MobileLeaderboardCard;
